import React from 'react';
import {
    DialogActions,
    DialogContentText,
    DialogContent,
    DialogTitle,
    Dialog,
    Alert,
    FormControlLabel,
    Checkbox,
    TextField,
    Box,
    InputAdornment,
    CircularProgress,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { validateCoupon } from '@openapi/coupons/coupons';

const ConfirmationModal = ({
    title,
    content,
    confirm,
    warningContent = false,
    sendNotificationFlag = false,
    showCouponInput = false,
    showId = null,
    customerEmail = '',
    orderAmount = 0,
    errorContent = false,
    showModal,
    MODAL_TYPE,
    maxWidth = 'sm',
    fullWidth = false,
    onCancel,
}) => {
    const [sendNotification, setSendNotification] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [couponValidating, setCouponValidating] = useState(false);
    const [couponValid, setCouponValid] = useState(null); // null = not checked, true = valid, false = invalid
    const [couponError, setCouponError] = useState('');
    const [couponDiscount, setCouponDiscount] = useState(null);
    const { t } = useTranslation();
    const closeAction = () => {
        if (onCancel) {
            onCancel();
        }
        showModal(MODAL_TYPE.NONE);
    };

    const handleVerifyCoupon = async () => {
        if (!couponCode.trim()) return;

        setCouponValidating(true);
        setCouponError('');
        setCouponValid(null);
        setCouponDiscount(null);

        try {
            const result = await validateCoupon({
                code: couponCode.trim().toUpperCase(),
                showId: showId,
                customerEmail: customerEmail,
                orderAmount: orderAmount,
            });

            if (result.valid) {
                setCouponValid(true);
                setCouponDiscount({
                    type: result.discountType,
                    value: result.discountValue,
                    amount: result.discountAmount,
                });
            } else {
                setCouponValid(false);
                setCouponError(result.message || t('coupons.invalid'));
            }
        } catch (err) {
            setCouponValid(false);
            setCouponError(err?.response?.data?.message || err?.message || t('coupons.invalid'));
        } finally {
            setCouponValidating(false);
        }
    };

    const handleCouponChange = (e) => {
        const value = e.target.value.toUpperCase();
        setCouponCode(value);
        // Reset validation state when code changes
        if (couponValid !== null) {
            setCouponValid(null);
            setCouponError('');
            setCouponDiscount(null);
        }
    };

    const formatDiscount = () => {
        if (!couponDiscount) return '';
        if (couponDiscount.type === 'percentage') {
            return `${couponDiscount.value}% off`;
        }
        return `$${(couponDiscount.amount / 100).toFixed(2)} off`;
    };

    return (
        <Dialog
            open
            onClose={closeAction}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth={maxWidth}
            fullWidth={fullWidth}
        >
            <DialogTitle id="alert-dialog-title">
                {title || t('confirmationModal.title')}
            </DialogTitle>
            <DialogContent>
                {typeof content === 'string' ? (
                    <DialogContentText id="alert-dialog-description">{content}</DialogContentText>
                ) : (
                    content
                )}
                {warningContent && (
                    <DialogContentText component="div" id="alert-dialog-warning">
                        <Alert sx={{ mt: 2 }} severity="warning">
                            {warningContent}
                        </Alert>
                    </DialogContentText>
                )}
                {errorContent && (
                    <DialogContentText component="div" id="alert-dialog-error">
                        <Alert sx={{ mt: 0.5 }} severity="error">
                            {errorContent}
                        </Alert>
                    </DialogContentText>
                )}
                {sendNotificationFlag && (
                    <DialogContentText component="div" id="dialog-send-notification">
                        <FormControlLabel
                            label={t('bookingitems.modifyTickets.sendNotification')}
                            control={
                                <Checkbox
                                    checked={sendNotification}
                                    onChange={(e) => setSendNotification(e.target.checked)}
                                    label={t('bookingitems.modifyTickets.sendNotification')}
                                />
                            }
                            sx={{ mt: 1 }}
                        />
                    </DialogContentText>
                )}
                {showCouponInput && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('coupons.applyCoupon')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField
                                size="small"
                                placeholder={t('coupons.enterCode')}
                                value={couponCode}
                                onChange={handleCouponChange}
                                disabled={couponValidating}
                                error={couponValid === false}
                                sx={{ flex: 1 }}
                                slotProps={{
                                    input: {
                                        endAdornment: couponValid === true && (
                                            <InputAdornment position="end">
                                                <CheckCircleIcon color="success" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <CommonButton
                                onClick={handleVerifyCoupon}
                                label={
                                    couponValidating ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        t('coupons.verify')
                                    )
                                }
                                buttonType="secondary"
                                disabled={!couponCode.trim() || couponValidating}
                            />
                        </Box>
                        {couponError && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {couponError}
                            </Typography>
                        )}
                        {couponValid === true && couponDiscount && (
                            <Alert severity="success" sx={{ mt: 1 }}>
                                {t('coupons.discountApplied', { discount: formatDiscount() })}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <CommonButton
                    onClick={closeAction}
                    autoFocus
                    label={t('common.close')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => {
                        // Pass both sendNotification and couponCode (only if validated)
                        confirm(sendNotification, couponValid === true ? couponCode : null);
                        showModal(MODAL_TYPE.NONE);
                    }}
                    label={t('common.yes')}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationModal;
